import { useMemo, useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import HotRankList from "@/components/feature/HotRankList";
import { hotList, siteInfo } from "@/mocks/site";
import type { HotItem } from "@/types/content";

const tabs = [
  { key: "all", label: "全部话题" },
  { key: "综合热度", label: "综合热度" },
  { key: "上升最快", label: "上升最快" },
  { key: "新上榜", label: "新上榜" },
];

const rankingsJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "CollectionPage",
      name: "热搜榜单 - 热搜吃瓜",
      url: "https://fvwyz.cn/rankings",
      inLanguage: "zh-CN",
      description: "按实时讨论量排序的热搜榜单，涵盖娱乐、综艺、影视与时尚等方向的当日热点。",
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://fvwyz.cn/" },
        { "@type": "ListItem", position: 2, name: "热搜榜单", item: "https://fvwyz.cn/rankings" },
      ],
    },
  ],
};

export default function Rankings() {
  const [active, setActive] = useState("all");

  const list: HotItem[] = useMemo(() => {
    if (active === "上升最快") {
      return hotList.filter((item) => item.trend === "up");
    }
    if (active === "新上榜") {
      return hotList.filter((item) => item.trend === "new");
    }
    if (active === "综合热度") {
      return [...hotList].sort((a, b) => parseInt(b.heat) - parseInt(a.heat));
    }
    return hotList;
  }, [active]);

  const now = new Date().toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" });

  return (
    <SiteLayout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(rankingsJsonLd) }} />

      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "热搜榜单" }]} />
          <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-4xl">热搜榜单</h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                这里汇总了当前讨论度最高的话题，按实时热度排序。榜单只反映此刻的关注度，事件本身可能仍在发展，我们会持续跟进后续进展。
              </p>
            </div>
            <div className="shrink-0 rounded-lg border border-background-300 bg-background-50 px-5 py-4">
              <p className="text-xs text-foreground-500">最近一次刷新</p>
              <p className="mt-1 font-heading text-lg font-bold text-primary-600">{now}</p>
              <p className="mt-1 text-xs text-foreground-500">在榜 {hotList.length} 个话题</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid w-full grid-cols-1 gap-6 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-3">
        <section className="lg:col-span-2">
          <div className="mb-5 flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActive(tab.key)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  active === tab.key
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            {list.length > 0 ? (
              <HotRankList items={list} />
            ) : (
              <p className="py-10 text-center text-sm text-foreground-500">暂时没有符合条件的话题，换个筛选试试。</p>
            )}
          </div>
        </section>

        <aside className="flex flex-col gap-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">榜单说明</h2>
            <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-600">
              <li className="flex gap-2">
                <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center text-secondary-600">
                  <i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>
                </span>
                热度值综合讨论量、增长速度与参与人数三个维度。
              </li>
              <li className="flex gap-2">
                <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center text-secondary-600">
                  <i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>
                </span>
                榜单排序为动态结果，话题热度回落后排名会自然下滑。
              </li>
              <li className="flex gap-2">
                <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center text-secondary-600">
                  <i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>
                </span>
                出现「爆」标记代表该话题在短时间内增长明显。
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
            <h2 className="font-heading text-base font-bold text-accent-900">提交你的线索</h2>
            <p className="mt-2 text-sm leading-relaxed text-accent-900/80">
              如果你发现了值得关注的新动向，欢迎通过邮件把线索发给我们，编辑会在核实后决定是否收录。
            </p>
            <a
              href={`mailto:${siteInfo.email}`}
              className="mt-4 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-secondary-950 transition-colors hover:bg-accent-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-mail-send-line w-4 h-4 flex items-center justify-center"></i>
              </span>
              {siteInfo.email}
            </a>
          </div>
        </aside>
      </div>
    </SiteLayout>
  );
}